// store/Reducers/balanceReducer.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/api';

// USER - Balansı gətir
export const get_user_balance = createAsyncThunk(
    'balance/get_user_balance',
    async (clientUserId, { rejectWithValue }) => {
        try {
            const url = clientUserId 
                ? `/user/balance?clientUserId=${clientUserId}`
                : '/user/balance';
            const { data } = await api.get(url);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: 'Xəta baş verdi' });
        }
    }
);

// USER - Balans artırma sorğusu
export const request_deposit = createAsyncThunk(
    'balance/request_deposit',
    async (formData, { rejectWithValue, getState }) => {
        try {
            // ✅ userInfo-dan id götür, formData-ya əlavə et
            const userInfo = getState().auth?.userInfo;
            if (userInfo?.id) {
                formData.append('clientUserId', userInfo.id);
            }

            const { data } = await api.post('/user/deposit-request', formData);
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: 'Xəta baş verdi' });
        }
    }
);

// USER - Balansdan ödəniş
export const pay_with_balance = createAsyncThunk(
    'balance/pay_with_balance',
    async ({ orderId, useCashback = false }, { rejectWithValue, getState }) => {
        const userInfo = getState().auth?.userInfo
        try {
            const { data } = await api.post('/user/purchase-with-balance', {
                orderId,
                clientUserId: userInfo?.id,
                useCashback
            })
            return data
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: 'Ödəniş uğursuz' })
        }
    }
)

// ADMIN - Bütün deposit sorğuları
export const get_all_deposits_admin = createAsyncThunk(
  'balance/get_all_deposits_admin',
  async ({ status, page, limit }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(
        `/admin/deposits/all?status=${status || ''}&page=${page}&limit=${limit}`
      );
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: 'Xəta baş verdi' });
    }
  }
);

// ADMIN - Deposit təsdiq et
export const approve_deposit = createAsyncThunk(
    'balance/approve_deposit',
    async (depositId, { rejectWithValue }) => {
        try {
            const { data } = await api.post('/admin/deposits/approve', { depositId });
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: 'Xəta baş verdi' });
        }
    }
);

// ADMIN - Deposit rədd et
export const reject_deposit = createAsyncThunk(
    'balance/reject_deposit',
    async ({ depositId, reason }, { rejectWithValue }) => {
        try {
            const { data } = await api.post('/admin/deposits/reject', { depositId, reason });
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: 'Xəta baş verdi' });
        }
    }
);

export const get_active_bank_infos = createAsyncThunk(
    'balance/get_active_bank_infos',
    async (_, { rejectWithValue }) => {
        try {
            const { data } = await api.get('/payment/bank-infos');
            return data;
        } catch (error) {
            return rejectWithValue(error.response?.data || { message: 'Xəta baş verdi' });
        }
    }
);

// Reducer
export const balanceReducer = createSlice({
    name: 'balance',
    initialState: {
        balance: 0,
        cashbackBalance: 0,
        transactions: [],
        deposits: [],
        totalDeposits: 0,
        activeBankInfos: [],
        loader: false,
        isBlocked: false,
        emailSecurityEnabled: false,
        successMessage: '',
        errorMessage: ''

    },
    reducers: {
        messageClear: (state) => {
            state.errorMessage = "";
            state.successMessage = "";
        },
        balanceUpdated: (state, { payload }) => {
            const nextBalance = payload?.newBalance ?? payload?.balance;
            if (nextBalance !== undefined) {
                state.balance = nextBalance;
            }
        }
    },
    extraReducers: (builder) => {
        builder
            // Get balance
            .addCase(get_user_balance.pending, (state) => {
                state.loader = true;
            })
            .addCase(get_user_balance.fulfilled, (state, { payload }) => {
    state.loader = false;
    state.balance = payload.balance || 0;
    state.transactions = payload.transactions || [];
    state.isBlocked = payload.isBlocked || false;
    state.emailSecurityEnabled = Boolean(payload.emailSecurityEnabled);
})
            .addCase(get_user_balance.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload?.message || 'Xəta baş verdi';
            })
            
            // Request deposit
            .addCase(request_deposit.pending, (state) => {
                state.loader = true;
            })
            .addCase(request_deposit.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.successMessage = payload.message;
            })
            .addCase(request_deposit.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload?.message || 'Xəta baş verdi';
            })
            
            // Pay with balance
            .addCase(pay_with_balance.pending, (state) => {
                state.loader = true;
            })
            .addCase(pay_with_balance.fulfilled, (state, { payload }) => {
    state.loader = false
    state.balance = payload.newBalance ?? state.balance
    if (payload.cashbackUsed !== undefined) {
        state.cashbackBalance = payload.newCashbackBalance ?? state.cashbackBalance
    }
    state.successMessage = payload.message || 'Ödəniş tamamlandı'
})
            .addCase(pay_with_balance.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload?.message || 'Ödəniş uğursuz';
            })
            
            // Admin - get all deposits
            .addCase(get_all_deposits_admin.pending, (state) => {
                state.loader = true;
            })
            .addCase(get_all_deposits_admin.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.deposits = payload.deposits || [];
                state.totalDeposits = payload.total || 0;
            })
            .addCase(get_all_deposits_admin.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload?.message || 'Məlumat yüklənmədi';
            })
            
            // Admin - approve
            .addCase(approve_deposit.pending, (state) => {
                state.loader = true;
            })
            .addCase(approve_deposit.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.successMessage = payload.message;
            })
            .addCase(approve_deposit.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload?.message || 'Xəta baş verdi';
            })
            
            // Admin - reject
            .addCase(reject_deposit.pending, (state) => {
                state.loader = true;
            })
            .addCase(reject_deposit.fulfilled, (state, { payload }) => {
                state.loader = false;
                state.successMessage = payload.message;
            })
            .addCase(reject_deposit.rejected, (state, { payload }) => {
                state.loader = false;
                state.errorMessage = payload?.message || 'Xəta baş verdi';
            })
            .addCase(get_active_bank_infos.fulfilled, (state, { payload }) => {
                state.activeBankInfos = payload.bankInfos || [];
            })
    }
});

export const { messageClear, balanceUpdated } = balanceReducer.actions;
export default balanceReducer.reducer;
